'use client';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RotateCcw, Bell, Settings } from 'lucide-react';
import { heatMapMetrics } from './heatmap-visualization';
import { environmentTypes } from './environment-sidebar';

interface ControlOverlayProps {
  selectedEnvironment: string;
  showHeatMap: boolean;
  setShowHeatMap: (show: boolean) => void;
  heatMapMetric: string;
  setHeatMapMetric: (metric: string) => void;
  showAlerts: boolean;
  setShowAlerts: (show: boolean) => void;
}

export default function ControlOverlay({
  selectedEnvironment,
  showHeatMap,
  setShowHeatMap,
  heatMapMetric,
  setHeatMapMetric,
  showAlerts,
  setShowAlerts,
}: ControlOverlayProps) {
  const currentEnvironment = environmentTypes[selectedEnvironment as keyof typeof environmentTypes];

  return (
    <>
      {/* Top Controls */}
      <div className="absolute top-4 left-4 flex gap-2">
        <Card className="border-border bg-background/95 backdrop-blur-sm">
          <CardContent className="p-2">
            <div className="flex items-center gap-2">
              <currentEnvironment.icon className="w-4 h-4" style={{ color: currentEnvironment.color }} />
              <span className="text-sm font-medium">{currentEnvironment.name}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Heat Map Controls */}
      <div className="absolute top-4 right-4 bg-background/95 backdrop-blur-sm rounded-lg shadow-sm border border-border p-3">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-sm font-medium">Heat Map</span>
          <Switch checked={showHeatMap} onCheckedChange={setShowHeatMap} />
        </div>
        {showHeatMap && (
          <Select value={heatMapMetric} onValueChange={setHeatMapMetric}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(heatMapMetrics).map(([key, metric]) => (
                <SelectItem key={key} value={key}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: metric.color }} />
                    {metric.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-4 left-4 bg-background/95 backdrop-blur-sm rounded-lg shadow-sm border border-border p-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm">
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset View
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowAlerts(!showAlerts)}>
            <Bell className="w-4 h-4 mr-1" />
            Alerts
          </Button>
          <Button variant="ghost" size="sm">
            <Settings className="w-4 h-4 mr-1" />
            Settings
          </Button>
        </div>
      </div>
    </>
  );
}
